import { SalesSegment } from './OnboardStore';

/**
 * One Edge product code priced under a payment method in one market and sales segment. The fees come from the method; the
 * code carries what differs per code. A merchant's stores are not set up the same way in every market, so the type and
 * validity of a code are stated where it is priced.
 */
export interface MerchantPriceProduct {
    /** Edge product code, max 16 characters. */
    productCode: string;
    /** Edge product type (account type), e.g. DirectInvoiceAccount. */
    productType: string;
    /** When the code becomes available, only for methods with validity dates (campaigns). */
    validFrom?: string;
    /** When the code stops being available, only for methods with validity dates. */
    validUntil?: string;
}

/**
 * What the merchant pays for one payment method in one market and sales segment, and the product codes that carry it
 * there. Listing a code makes it active in the market and segment with the fees of the method. Percentages are in percent
 * (2.5 = 2.5 %), fixed fees are amounts in the settlement currency of the market. A missing fee is not priced in Merchant
 * Hub; Edge then falls back to the settlement setting of the store.
 */
export interface MerchantMethodPrices {
    paymentMethod: string;
    /** Percentage of the purchase amount, 0–100. */
    serviceFee?: number;
    /** Fixed amount per transaction. */
    transactionFee?: number;
    /** Percentage of the purchase amount of a recurring purchase; only for methods with recurring purchases. */
    recurringServiceFee?: number;
    /** Fixed amount per recurring purchase; only for methods with recurring purchases. */
    recurringTransactionFee?: number;
    /** Percentage of the refunded amount, 0–100; missing leaves refunds without a service fee. */
    refundServiceFee?: number;
    /** Fixed amount per refund; missing leaves refunds without a transaction fee. */
    refundTransactionFee?: number;
    /** The product codes active for the method in the market and segment; at least one. */
    products: MerchantPriceProduct[];
}

/** The prices of one market (country): per sales segment the active methods with their fees and codes. */
export interface MerchantPriceMarket {
    countryCode: string;
    /** Settlement currency of the market; filled in by the API, must match the country when sent. */
    currency?: string;
    segments?: Partial<Record<SalesSegment, MerchantMethodPrices[]>>;
}

/**
 * The price list of a merchant, shaped like the product appendix: per market and segment the active
 * methods with their fees and the codes that carry them. Every market is complete on its own. This is what
 * GET organizations/{id}/merchants/{countryCode}/{organizationNumber}/prices returns and what PUT replaces.
 */
export interface MerchantPriceList {
    markets: MerchantPriceMarket[];
}

export const emptyPriceList: MerchantPriceList = { markets: [] };

/** Outcome of syncing the prices of a merchant to one of its stores in Edge. */
export type StorePriceSyncOutcome = 'Completed' | 'NoPricesForStore' | 'Failed';

/** Outcome of syncing one product of a store in Edge with the prices of the merchant. */
export type ProductPriceSyncOutcome =
    | 'Priced'
    | 'AlreadyPriced'
    | 'Repriced'
    | 'Created'
    | 'Deactivated'
    | 'Failed';

export interface ProductPriceSyncResult {
    productCode: string;
    outcome: ProductPriceSyncOutcome;
    /** More about the outcome: how the prices differed, what changed on the product, or why Edge failed. */
    detail?: string;
}

/** What the price sync did to one store of the merchant, as POST .../prices/sync returns it. */
export interface StorePriceSyncResult {
    storeId: string;
    countryCode: string;
    salesSegment: string;
    outcome: StorePriceSyncOutcome;
    products: ProductPriceSyncResult[];
    detail?: string;
}
